<template>
  <div class="header-wrapper">
    <PageLoader v-bind:storage="appLoading" />
    <ServerError v-if="ServerError" />
    
    <!-- Main Header Row -->
    <div class="main-header px-4">
      <v-container class="pa-0 fill-height" fluid>
        <v-layout wrap align-center>
          <!-- Logo Section -->
          <v-flex xs2 sm2 md2 lg1 class="d-flex align-center justify-start">
            <router-link to="/" class="d-flex align-center">
              <v-img 
                src="./../../assets/newlogo.png" 
                :height="$vuetify.breakpoint.xsOnly ? '35px' : '45px'" 
                :width="$vuetify.breakpoint.xsOnly ? '35px' : '45px'"
                contain 
                class="logo-img"
              ></v-img>
            </router-link>
          </v-flex>

          <!-- Search Bar (Desktop/Tablet) -->
          <v-flex hidden-xs-only sm7 md8 lg9 class="px-2 px-md-4">
            <div class="search-box">
              <v-icon class="search-icon">mdi-magnify</v-icon>
              <v-combobox
                v-model="keyword"
                :items="items"
                :loading="isLoading"
                :search-input.sync="search"
                hide-no-data
                hide-selected
                item-text="productname"
                item-value="_id"
                placeholder="Search for products, categories or brands..."
                dense
                solo
                flat
                hide-details
                return-object
                @change="searchProduct()"
                @keyup.enter.native="searchProduct()"
                class="search-autocomplete"
                :menu-props="{ offsetY: true, bottom: true, contentClass: 'search-dropdown-menu' }"
              >
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title class="dropdown-item-title">{{ item.productname }}</v-list-item-title>
                    <v-list-item-subtitle class="dropdown-item-subtitle">
                      Professional Choice • Fresh Farm Produce
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-icon small color="#cbd5e1">mdi-magnify</v-icon>
                  </v-list-item-action>
                </template>
              </v-combobox>
              <v-btn 
                depressed 
                color="#3EA56E" 
                dark 
                class="search-btn hidden-sm-and-down"
                @click="searchProduct()"
              >
                Search
              </v-btn>
            </div>
          </v-flex>

          <!-- Account & Actions Section -->
          <v-flex xs10 sm3 md2 lg2 class="d-flex justify-end align-center">
            <div class="actions-group" style="width:100%; min-width: 120px;">
              <AccountMenu />
            </div>
          </v-flex>

          <!-- Mobile Search (Only on XS) -->
          <v-flex xs12 hidden-sm-and-up class="pt-2 pb-3 px-1">
            <div class="search-box mobile">
              <v-combobox
                v-model="keyword"
                :items="items"
                :loading="isLoading"
                :search-input.sync="search"
                hide-no-data
                hide-selected
                item-text="productname"
                item-value="_id"
                placeholder="Search products..."
                dense
                solo
                flat
                hide-details
                return-object
                @change="searchProduct()"
                @keyup.enter.native="searchProduct()"
                class="search-autocomplete"
                :menu-props="{ offsetY: true, bottom: true, contentClass: 'search-dropdown-menu' }"
              >
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title class="dropdown-item-title">{{ item.productname }}</v-list-item-title>
                  </v-list-item-content>
                </template>
              </v-combobox>
            </div>
          </v-flex>
        </v-layout>
      </v-container>
    </div>

    <!-- Categories Navigation Row (The "Another Section") -->
    <nav class="categories-nav" v-if="appCat && appCat.length > 0">
      <v-container class="pa-0" fluid>
        <div class="categories-scroll-wrapper">
          <div class="categories-list d-flex align-center px-4">
            <div 
              v-for="(item, i) in appCat" 
              :key="i"
              class="category-link"
              :class="{ 'active': $route.params.MainCategory == item.urlname }"
              @click="fetchName(item)"
            >
              <span class="category-label">{{ item.name }}</span>
            </div>
          </div>
        </div>
      </v-container>
    </nav>
  </div>
</template>

<script>
import AccountMenu from "./accountMenu";
import axios from "axios";

export default {
  components: {
    AccountMenu,
  },
  data() {
    return {
      searchKey: "",
      keyword: null,
      items: [],
      isLoading: false,
      search: null,
      page: 1,
      limit: 20,
      ServerError: false,
      appLoading: false,
      searchTimer: null,
    };
  },
  watch: {
    search(val) {
      // If the user types something different, clear the old selected keyword object
      if (this.keyword && typeof this.keyword === 'object' && this.keyword.productname !== val) {
        this.keyword = null;
      }

      if (!val || val.length < 2) {
        this.items = [];
        return;
      }
      
      if (this.searchTimer) clearTimeout(this.searchTimer);
      
      this.searchTimer = setTimeout(() => {
        this.isLoading = true;
        axios({
          method: "POST",
          url: "/search",
          headers: {
            token: localStorage.getItem("token"),
          },
          data: {
            keyword: val,
          },
        })
          .then((response) => {
            if (response.data.status == true) {
              this.items = response.data.data;
            }
          })
          .catch((err) => {
            console.log(err);
          })
          .finally(() => (this.isLoading = false));
      }, 400); // 400ms debounce
    },
  },
  computed: {
    appLogin() {
      return this.$store.state.isLoggedIn;
    },
    appCat() {
      return this.$store.state.categories.data;
    },
    mainCat() {
      return this.$store.state.SelectedIDS.MainCatId;
    },
  },
  mounted() {
    this.searchKey = this.$route.query.searchKey || "";
  },
  methods: {
    appLogout() {
      this.$store.commit("logoutUser", true);
    },
    fetchName(item) {
      this.$store.commit("updateMainCatId", item._id);
      localStorage.setItem("mainCategory", item._id);
      this.$store.commit("updateCatId", "");
      this.$store.commit("updateSubCatId", "");
      this.$router.push("/products/" + item.urlname);
    },
    searchProduct() {
      // Get search term from either the selected keyword object or the typed search text
      let searchTerm = "";
      if (this.keyword) {
        searchTerm = typeof this.keyword === 'string' ? this.keyword : this.keyword.productname;
      } else if (this.search) {
        searchTerm = this.search;
      }

      if (!searchTerm) return;
      
      this.appLoading = true;
      var headers1 = {};
      var data = {
        keyword: searchTerm,
        page: this.page,
        limit: this.limit,
      };
      if (this.appLogin) {
        headers1 = {
          "x-auth-token": localStorage.getItem("token"),
        };
      } else {
        data["guestid"] = localStorage.getItem("guestId");
      }
      axios({
        method: "POST",
        url: "/product/search",
        data: data,
        headers: headers1,
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status) {
            this.$store.commit("getSearchResults", response.data.data);
            this.$store.commit("getInstock", response.data.inStock);
            this.$store.commit("getOutstock", response.data.outOffstock);
            this.$router.push(`/products?type='search'&q=${encodeURIComponent(searchTerm)}`).catch(()=>{});
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
  },
};
</script>

<style scoped>
.header-wrapper {
  background: #ffffff;
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
}

.main-header {
  min-height: 80px;
  display: flex;
  align-items: center;
  border-bottom: 1px solid #f1f5f9;
}

@media (max-width: 600px) {
  .main-header {
    min-height: auto;
    padding-top: 10px;
  }

  .actions-group {
    overflow: visible !important;
  }
}

.logo-img {
  transition: transform 0.3s ease;
}

.logo-img:hover {
  transform: scale(1.05);
}

/* Search Box Styles */
.search-box {
  display: flex;
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 4px 4px 4px 16px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  width: 100%;
}

.search-box:focus-within {
  background: #ffffff;
  border-color: #3EA56E;
  box-shadow: 0 0 0 4px rgba(62, 165, 110, 0.1);
}

.search-icon {
  color: #94a3b8 !important;
  font-size: 20px;
}

.search-autocomplete {
  flex: 1;
  min-width: 0;
}

.search-autocomplete ::v-deep .v-input__slot {
  box-shadow: none !important;
  background: transparent !important;
  padding: 0 !important;
}

.search-autocomplete ::v-deep .v-text-field__slot input {
  padding: 10px 0;
  font-size: 15px;
}

.search-btn {
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0.3px;
  border-radius: 8px !important;
  height: 42px !important;
  padding: 0 24px !important;
}

/* Dropdown Professional Styling */
::v-deep .search-dropdown-menu {
  border-radius: 12px !important;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.12) !important;
  border: 1px solid #f1f5f9 !important;
  margin-top: 8px !important;
  overflow: hidden;
}

.dropdown-item-title {
  font-size: 14px !important;
  font-weight: 500 !important;
  color: #1e293b !important;
  font-family: 'Poppins', sans-serif;
}

.dropdown-item-subtitle {
  font-size: 11px !important;
  color: #94a3b8 !important;
  font-weight: 400;
  margin-top: 2px;
}

::v-deep .v-list-item--active {
  background-color: #f0fdf4 !important;
  color: #3EA56E !important;
}

::v-deep .v-list-item--active .dropdown-item-title {
  color: #3EA56E !important;
}

.search-box.mobile {
  border-radius: 8px;
  padding: 2px 12px;
}

/* Categories Nav Styles */
.categories-nav {
  background: #ffffff;
  border-bottom: 1px solid #f1f5f9;
}

.categories-scroll-wrapper {
  overflow-x: auto;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none;  /* IE and Edge */
}

.categories-scroll-wrapper::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

.categories-list {
  height: 50px;
  width: 100%;
  justify-content: flex-start;
}

.category-link {
  padding: 0 24px;
  height: 100%;
  display: flex;
  align-items: center;
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.category-label {
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  font-family: 'Poppins', sans-serif;
}

.category-link:hover .category-label {
  color: #3EA56E;
}

.category-link.active .category-label {
  color: #3EA56E;
}

.category-link.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 15%;
  right: 15%;
  height: 3px;
  background: #3EA56E;
  border-radius: 3px 3px 0 0;
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .category-link {
    padding: 0 16px;
  }
  .category-label {
    font-size: 12px;
  }
}
</style>
